import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Activity,
  Users,
  Wifi,
  WifiOff,
  Clock,
  Zap,
  ArrowRight,
  Play,
  Ban,
  Server
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DispatchSync = ({ authToken }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [connectionTest, setConnectionTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [triggering, setTriggering] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, connRes] = await Promise.all([
        axios.get(`${API_URL}/api/exchange/dispatch/sync/status`),
        axios.get(`${API_URL}/api/exchange/dispatch/connection-test`)
      ]);
      setSyncStatus(statusRes.data);
      setConnectionTest(connRes.data);
    } catch (error) {
      console.error('Failed to fetch dispatch data:', error);
      toast.error('Failed to load dispatch integration data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleTriggerExpiry = async (driverId, credentialType) => {
    setTriggering(`expire-${driverId}`);
    try {
      const response = await axios.post(
        `${API_URL}/api/exchange/dispatch/trigger/expire-credential?driver_id=${driverId}&credential_type=${credentialType}&reason=Admin%20triggered%20expiry`
      );
      toast.success(`Triggered ${credentialType} expiry for ${driverId} - Driver will be locked out in OVA Dispatch`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to trigger credential expiry');
    } finally {
      setTriggering(null);
    }
  };

  const handleTriggerRenewal = async (driverId, credentialType) => {
    setTriggering(`renew-${driverId}`);
    try {
      const response = await axios.post(
        `${API_URL}/api/exchange/dispatch/trigger/renew-credential?driver_id=${driverId}&credential_type=${credentialType}&reason=Admin%20triggered%20renewal`
      );
      toast.success(`Triggered ${credentialType} renewal for ${driverId} - Checking compliance for reactivation`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to trigger credential renewal');
    } finally {
      setTriggering(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/api/exchange/dispatch/sync/all`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Synced all drivers with OVA Dispatch');
      await fetchData();
    } catch (error) {
      toast.error('Failed to sync all drivers');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
      case 'active':
        return 'text-green-400 bg-green-500/20';
      case 'warning':
        return 'text-amber-400 bg-amber-500/20';
      case 'suspended':
        return 'text-red-400 bg-red-500/20';
      case 'en_route':
      case 'on_trip':
        return 'text-blue-400 bg-blue-500/20';
      case 'offline':
        return 'text-slate-400 bg-slate-500/20';
      default:
        return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'suspended':
        return <XCircle className="w-4 h-4" />;
      case 'en_route':
      case 'on_trip':
        return <Activity className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const isConnected = connectionTest?.connection_status === 'connected';

  return (
    <div className="space-y-6" data-testid="dispatch-sync-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-cyan-400" />
            OVA Exchange ↔ OVA Dispatch Integration
          </h2>
          <p className="text-slate-400 mt-1">
            Production API integration between credential vault and dispatch operations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 transition-colors"
          >
            {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Sync All Drivers
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl p-4 border ${isConnected ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
        >
          <div className="flex items-center gap-3">
            {isConnected ? (
              <Wifi className="w-8 h-8 text-green-400" />
            ) : (
              <WifiOff className="w-8 h-8 text-red-400" />
            )}
            <div>
              <p className="text-slate-400 text-sm">Connection</p>
              <p className={`font-semibold ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
                {isConnected ? 'LIVE' : 'DISCONNECTED'}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <Server className="w-8 h-8 text-purple-400" />
            <div>
              <p className="text-slate-400 text-sm">Dispatch API</p>
              <p className="text-white font-semibold text-sm truncate max-w-[150px]" title={connectionTest?.dispatch_api_url}>
                {connectionTest?.dispatch_api_url?.replace('https://', '')}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-cyan-400" />
            <div>
              <p className="text-slate-400 text-sm">Dispatch Drivers</p>
              <p className="text-white font-semibold">
                {connectionTest?.drivers_count || syncStatus?.total_dispatch_drivers || 0}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-amber-400" />
            <div>
              <p className="text-slate-400 text-sm">Recent Syncs</p>
              <p className="text-white font-semibold">
                {syncStatus?.recent_syncs?.length || 0} events
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Connection Error */}
      {!isConnected && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-red-400 font-semibold">Connection Error</p>
              <p className="text-red-300/80 text-sm mt-1">
                Unable to connect to OVA Dispatch at <code className="bg-slate-800 px-2 py-0.5 rounded">{connectionTest?.dispatch_api_url}</code>. 
                Check that the Dispatch API is running and accessible.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Drivers Grid */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-cyan-400" />
          Driver Compliance Status (Dispatch ↔ Exchange)
        </h3>
        
        {syncStatus?.driver_statuses?.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No drivers found in OVA Dispatch</p>
        ) : (
          <div className="space-y-3">
            {syncStatus?.driver_statuses?.map((driver, index) => (
              <motion.div
                key={driver.driver_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-900/50 rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{driver.name || 'Unknown'}</p>
                    <p className="text-slate-500 text-sm font-mono">{driver.driver_id}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {/* Dispatch Status */}
                  <div className="text-center">
                    <p className="text-slate-500 text-xs mb-1">Dispatch</p>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(driver.dispatch_status)}`}>
                      {getStatusIcon(driver.dispatch_status)}
                      {driver.dispatch_status}
                    </span>
                  </div>

                  <ArrowRight className="w-4 h-4 text-slate-600" />

                  {/* Exchange Compliance */}
                  <div className="text-center">
                    <p className="text-slate-500 text-xs mb-1">Exchange</p>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(driver.exchange_compliance)}`}>
                      {getStatusIcon(driver.exchange_compliance)}
                      {driver.exchange_compliance}
                    </span>
                  </div>

                  {/* Issues Badge */}
                  {driver.issues_count > 0 && (
                    <div className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs">
                      {driver.issues_count} issues
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTriggerExpiry(driver.driver_id, 'drug_test')}
                      disabled={triggering === `expire-${driver.driver_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-xs"
                      title="Trigger Drug Test Failure - Immediate Lockout in OVA Dispatch"
                    >
                      {triggering === `expire-${driver.driver_id}` ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Ban className="w-3 h-3" />
                      )}
                      Lockout
                    </button>
                    <button
                      onClick={() => handleTriggerRenewal(driver.driver_id, 'license')}
                      disabled={triggering === `renew-${driver.driver_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-xs"
                      title="Trigger Credential Renewal - Check Compliance for Reactivation"
                    >
                      {triggering === `renew-${driver.driver_id}` ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                      Activate
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Sync Logs */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-400" />
          Recent Sync Activity (Exchange → Dispatch)
        </h3>

        {syncStatus?.recent_syncs?.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No sync activity yet. Trigger a credential event to see real API calls.</p>
        ) : (
          <div className="space-y-2">
            {syncStatus?.recent_syncs?.map((log, index) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${
                    log.action === 'suspend' ? 'bg-red-400' : 
                    log.action === 'warn' ? 'bg-amber-400' : 
                    log.action === 'activate' ? 'bg-green-400' : 'bg-slate-400'
                  }`} />
                  <span className="text-white font-mono">{log.driver_id}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    log.action === 'suspend' ? 'bg-red-500/20 text-red-400' : 
                    log.action === 'warn' ? 'bg-amber-500/20 text-amber-400' : 
                    log.action === 'activate' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'
                  }`}>
                    {log.action}
                  </span>
                  <ArrowRight className="w-3 h-3 text-slate-600" />
                  <span className="text-cyan-400 text-xs">PUT /api/drivers/{log.driver_id}/status</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-400 text-xs max-w-xs truncate">{log.reason}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${log.result?.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {log.result?.success ? 'SUCCESS' : 'FAILED'}
                  </span>
                  <span className="text-slate-500 text-xs">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Grace Periods Info */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-400" />
          Compliance Grace Periods
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          When a credential expires in OVA Exchange, these grace periods determine how quickly the driver is locked out in OVA Dispatch.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { type: 'License', days: 3 },
            { type: 'Insurance', days: 1 },
            { type: 'Drug Test', days: 0 },
            { type: 'Background Check', days: 0 },
            { type: 'Medical Cert', days: 7 },
            { type: 'Vehicle Inspection', days: 3 },
            { type: 'NEMT Certification', days: 7 },
            { type: 'CPR Certification', days: 14 },
          ].map((item) => (
            <div key={item.type} className="bg-slate-900/50 rounded-lg p-3">
              <p className="text-slate-400 text-xs">{item.type}</p>
              <p className={`font-semibold ${item.days === 0 ? 'text-red-400' : 'text-white'}`}>
                {item.days === 0 ? 'IMMEDIATE LOCKOUT' : `${item.days} day grace`}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
